import type { PresetFile } from './preset';

/**
 * IndexedDB storage for presets.
 *
 * Separate database from the font cache: font binaries are large and disposable, while
 * presets are small and precious, and mixing them would mean a schema change on one
 * risks the other.
 */
const DB_NAME = 'typo-studio-presets';
const DB_VERSION = 1;
const STORE = 'presets';

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
  return dbPromise;
}

export interface StoredPreset {
  id: string;
  name: string;
  createdAt: string;
  file: PresetFile;
}

export async function listPresets(): Promise<StoredPreset[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    req.onsuccess = () =>
      resolve(
        (req.result as StoredPreset[]).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
      );
    req.onerror = () => resolve([]);
  });
}

export async function savePreset(file: PresetFile): Promise<StoredPreset> {
  const entry: StoredPreset = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    name: file.name,
    createdAt: file.createdAt,
    file,
  };
  const db = await openDb();
  if (!db) return entry;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
  return entry;
}

export async function deletePreset(id: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}
